import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MoreVertical, X } from "lucide-react";
import { toast } from "react-hot-toast";
import PolicyFormWizardShell from "../policy-wizard/PolicyFormWizardShell";
import PolicyWorkflowStepSection from "../policy-wizard/PolicyWorkflowStepSection";
import PolicyApplicabilityStepSection from "../policy-wizard/PolicyApplicabilityStepSection";
import PolicyListToolbar from "../policy-wizard/PolicyListToolbar";
import { policyListRowActionClass, policyListStatusClass } from "../policy-wizard/PolicyListTableHeader";
import { PolicyHintField } from "../policy-wizard/PolicyFieldHint";
import { LEAVE_ENCASHMENT_POLICY_STEPS } from "../policy-wizard/policyWizardSteps";
import {
  createPolicyWizardNav,
  validateApplicabilityStep,
  validateBasicsStep,
  validateWorkflowStep,
} from "../policy-wizard/policyWizardValidation";
import usePolicyCompanyRoles from "../policy-wizard/usePolicyCompanyRoles";
import { payrollPolicyService } from "../../../services/api";
import {
  ENCASH_WORKFLOW_TABS,
  LOAN_APPROVAL_METHODS,
  packAssignedTo,
  pwInput,
  unpackAssignedTo,
} from "./payrollJsonPolicyForm";

function emptyForm() {
  return {
    policyName: "",
    status: "active",
    effectiveDate: new Date().toISOString().slice(0, 10),
    allEmployees: true,
    branch: "",
    department: "",
    designation: "",
    grade: "",
    category: "",
    subDepartment: "",
    location: "",
    employmentTypes: [],
    individualEmployeeIds: "",
    approvalMethod: "workflow",
    workflowSteps: [],
    reminderDays: 3,
    minServiceMonths: 12,
    minBalanceDays: 0,
    minEncashDays: 1,
    maxEncashDays: "",
    maxAnnualDays: "",
    carryForwardEligible: true,
    allowDuringEmployment: true,
    allowOnSeparation: true,
    method: "on_demand",
    annualPayMonth: "03",
    employeeCanRequest: true,
    autoProcessAnnual: false,
    includePendingLeave: false,
    salaryBasis: "basic",
    perDayMethod: "monthly_div_30",
    encashmentEarning: "",
    reversalRecovery: "",
    fnfEarning: "",
    inServiceFullyTaxable: true,
    separationApplyExemption: true,
    exemptionRegime: "non_government",
    notifiedExemptionCap: "",
  };
}

function rowToForm(row) {
  const r = row.rulesJson || {};
  const w = row.workflowJson || {};
  const p = row.payrollMapJson || {};
  const t = row.taxJson || {};
  return {
    ...emptyForm(),
    ...unpackAssignedTo(row.assignedTo),
    id: row.id,
    policyName: row.name || "",
    status: row.status || "active",
    effectiveDate: String(row.effectiveDate || "").slice(0, 10),
    approvalMethod: w.approvalMethod || "workflow",
    workflowSteps: Array.isArray(w.workflowSteps) ? w.workflowSteps : [],
    reminderDays: w.reminderDays || 3,
    minServiceMonths: r.minServiceMonths ?? 12,
    minBalanceDays: r.minBalanceDays ?? 0,
    minEncashDays: r.minEncashDays ?? 1,
    maxEncashDays: r.maxEncashDays ?? "",
    maxAnnualDays: r.maxAnnualDays ?? "",
    carryForwardEligible: r.carryForwardEligible !== false,
    allowDuringEmployment: r.allowDuringEmployment !== false,
    allowOnSeparation: r.allowOnSeparation !== false,
    method: r.method || "on_demand",
    annualPayMonth: r.annualPayMonth || "03",
    employeeCanRequest: r.employeeCanRequest !== false,
    autoProcessAnnual: !!r.autoProcessAnnual,
    includePendingLeave: !!r.includePendingLeave,
    salaryBasis: r.salaryBasis || "basic",
    perDayMethod: r.perDayMethod || "monthly_div_30",
    encashmentEarning: p.encashmentEarning || "",
    reversalRecovery: p.reversalRecovery || "",
    fnfEarning: p.fnfEarning || "",
    inServiceFullyTaxable: t.inServiceFullyTaxable !== false,
    separationApplyExemption: t.separationApplyExemption !== false,
    exemptionRegime: t.exemptionRegime || "non_government",
    notifiedExemptionCap: t.notifiedExemptionCap ?? "",
  };
}

function formToPayload(form) {
  return {
    id: form.id,
    name: form.policyName,
    status: form.status,
    effectiveDate: form.effectiveDate,
    assignedTo: packAssignedTo(form),
    rulesJson: {
      minServiceMonths: Number(form.minServiceMonths) || 0,
      minBalanceDays: Number(form.minBalanceDays) || 0,
      minEncashDays: Number(form.minEncashDays) || 1,
      maxEncashDays: form.maxEncashDays === "" ? null : Number(form.maxEncashDays),
      maxAnnualDays: form.maxAnnualDays === "" ? null : Number(form.maxAnnualDays),
      carryForwardEligible: !!form.carryForwardEligible,
      allowDuringEmployment: !!form.allowDuringEmployment,
      allowOnSeparation: !!form.allowOnSeparation,
      method: form.method,
      annualPayMonth: form.annualPayMonth,
      employeeCanRequest: !!form.employeeCanRequest,
      autoProcessAnnual: !!form.autoProcessAnnual,
      includePendingLeave: !!form.includePendingLeave,
      salaryBasis: form.salaryBasis,
      perDayMethod: form.perDayMethod,
    },
    workflowJson: {
      approvalMethod: form.approvalMethod,
      workflowSteps: form.workflowSteps || [],
      reminderDays: Number(form.reminderDays) || 3,
    },
    payrollMapJson: {
      encashmentEarning: form.encashmentEarning,
      reversalRecovery: form.reversalRecovery,
      fnfEarning: form.fnfEarning,
    },
    taxJson: {
      inServiceFullyTaxable: !!form.inServiceFullyTaxable,
      separationApplyExemption: !!form.separationApplyExemption,
      exemptionRegime: form.exemptionRegime,
      notifiedExemptionCap:
        form.notifiedExemptionCap === "" ? null : Number(form.notifiedExemptionCap),
    },
  };
}

export default function LeaveEncashmentPolicyComponent({
  darkMode = false,
  canEdit = true,
  branchesList = [],
  departmentsList = [],
  designationsList = [],
  gradesList = [],
  categoriesList = [],
  subDepartmentsList = [],
}) {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [step, setStep] = useState(1);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [workflowTab, setWorkflowTab] = useState("approval");
  const [actionMenu, setActionMenu] = useState(null);
  const { policyRoleOptions, defaultPolicyRoleValue } = usePolicyCompanyRoles();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await payrollPolicyService.listLeaveEncashmentPolicies();
      setPolicies(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load encashment policies");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const listed = useMemo(() => {
    const q = search.trim().toLowerCase();
    return policies.filter((p) => {
      if (!showInactive && p.status === "inactive") return false;
      if (q && !String(p.name || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [policies, showInactive, search]);

  const validateStep = (n) => {
    if (n === 1) return validateBasicsStep(form);
    if (n === 4) return validateWorkflowStep(form);
    if (n === 5) return validateApplicabilityStep(form);
    return null;
  };

  const savePolicy = async () => {
    const err = validateStep(5) || validateBasicsStep(form);
    if (err) {
      setSaveError(err);
      return;
    }
    setSaving(true);
    try {
      const res = await payrollPolicyService.saveLeaveEncashmentPolicy(formToPayload(form));
      if (res?.success === false) throw new Error(res.message || "Save failed");
      toast.success(isEditing ? "Encashment policy updated" : "Encashment policy saved");
      setIsFormOpen(false);
      load();
    } catch (e) {
      setSaveError(e.response?.data?.message || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const { handleStepClick, footer } = createPolicyWizardNav({
    policyFormStep: step,
    steps: LEAVE_ENCASHMENT_POLICY_STEPS,
    validateStep,
    setPolicyFormStep: setStep,
    setPolicySaveError: setSaveError,
    onCancel: () => setIsFormOpen(false),
    onPreviewSave: savePolicy,
    darkMode,
  });

  const openNew = () => {
    setForm(emptyForm());
    setIsEditing(false);
    setStep(1);
    setSaveError(null);
    setIsFormOpen(true);
  };

  const openEdit = (row) => {
    setForm(rowToForm(row));
    setIsEditing(true);
    setStep(1);
    setSaveError(null);
    setActionMenu(null);
    setIsFormOpen(true);
  };

  const remove = async (row) => {
    setActionMenu(null);
    if (!canEdit) return;
    try {
      await payrollPolicyService.deleteLeaveEncashmentPolicy(row.id);
      toast.success("Policy deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Delete failed");
    }
  };

  const addWorkflowStep = (key) => {
    setForm((prev) => ({
      ...prev,
      [key]: [
        ...(prev[key] || []),
        { role: defaultPolicyRoleValue || "manager", slaDays: 3, notifyOnly: false },
      ],
    }));
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PolicyListToolbar
        title="Leave encashment policies"
        newLabel="New Encashment Policy"
        onNew={openNew}
        searchValue={search}
        onSearchChange={(e) => setSearch(e.target.value)}
        showInactive={showInactive}
        onToggleInactive={() => setShowInactive((v) => !v)}
      />
      {loading ? (
        <p className="py-6 text-center text-sm text-[hsl(var(--ds-muted-fg))]">Loading…</p>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-[hsl(var(--ds-border))]">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]">
              <tr>
                <th className="w-12 px-4 py-3">#</th>
                <th className="px-4 py-3">Policy Name</th>
                <th className="px-4 py-3">Method</th>
                <th className="px-4 py-3">Effective</th>
                <th className="px-4 py-3">Approval</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {listed.map((row, idx) => (
                <tr key={row.id} className="border-t border-[hsl(var(--ds-border))]">
                  <td className="px-4 py-3">{idx + 1}</td>
                  <td className="px-4 py-3 font-medium">{row.name}</td>
                  <td className="px-4 py-3">{row.rulesJson?.method || "—"}</td>
                  <td className="px-4 py-3">{row.effectiveDate || "—"}</td>
                  <td className="px-4 py-3 capitalize">
                    {(row.workflowJson?.approvalMethod || "workflow").replace("_", " ")}
                  </td>
                  <td className="px-4 py-3">
                    <span className={policyListStatusClass(row.status === "active")}>{row.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      className={policyListRowActionClass}
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setActionMenu({
                          row,
                          top: rect.bottom + 4,
                          left: Math.max(8, rect.right - 180),
                        });
                      }}
                    >
                      <MoreVertical size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!listed.length && (
            <p className="py-6 text-center text-sm text-[hsl(var(--ds-muted-fg))]">
              {search.trim()
                ? "No policies match your search."
                : 'No policies. Click "New Encashment Policy" to create one.'}
            </p>
          )}
        </div>
      )}

      {actionMenu && (
        <div className="fixed inset-0 z-[10010]" onClick={() => setActionMenu(null)}>
          <div
            className="absolute z-[10011] w-44 rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] py-1 shadow-lg"
            style={{ top: actionMenu.top, left: actionMenu.left }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="block w-full px-3 py-2 text-left text-sm hover:bg-[hsl(var(--ds-muted))]"
              onClick={() => openEdit(actionMenu.row)}
            >
              Edit
            </button>
            {canEdit && (
              <button
                type="button"
                className="block w-full px-3 py-2 text-left text-sm text-[hsl(var(--ds-danger))] hover:bg-[hsl(var(--ds-muted))]"
                onClick={() => remove(actionMenu.row)}
              >
                Delete
              </button>
            )}
          </div>
        </div>
      )}

      {isFormOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/45 p-4 backdrop-blur-[2px]">
          <div className="flex h-[min(90vh,720px)] w-[min(92vw,780px)] flex-col overflow-hidden rounded-2xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-2xl">
            <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] px-5 py-4">
              <h3 className="text-[15px] font-semibold">
                {isEditing ? "Edit leave encashment policy" : "New leave encashment policy"}
              </h3>
              <button type="button" onClick={() => setIsFormOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col">
              <PolicyFormWizardShell
                darkMode={darkMode}
                steps={LEAVE_ENCASHMENT_POLICY_STEPS}
                currentStep={step}
                onStepClick={handleStepClick}
                policySaveError={saveError}
                footer={
                  <>
                    {footer}
                    {saving && (
                      <span className="w-full text-right text-xs text-[hsl(var(--ds-muted-fg))] sm:w-auto">
                        Saving…
                      </span>
                    )}
                  </>
                }
              >
                {step === 1 && (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <PolicyHintField label="Policy name" hint="Internal label, e.g. In-service EL encashment.">
                      <input
                        className={pwInput}
                        value={form.policyName}
                        onChange={(e) => setForm((p) => ({ ...p, policyName: e.target.value }))}
                      />
                    </PolicyHintField>
                    <PolicyHintField
                      label="Effective from"
                      hint="First date this policy can apply. Finalised months stay locked until sequential unlock."
                    >
                      <input
                        type="date"
                        className={pwInput}
                        value={form.effectiveDate}
                        onChange={(e) => setForm((p) => ({ ...p, effectiveDate: e.target.value }))}
                      />
                    </PolicyHintField>
                    <PolicyHintField label="Status" hint="Inactive policies are not offered on Request.">
                      <select
                        className={pwInput}
                        value={form.status}
                        onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </PolicyHintField>
                    <PolicyHintField
                      label="Method"
                      hint="On demand = anytime (subject to limits). Annual = one window. Separation = F&F only."
                    >
                      <select
                        className={pwInput}
                        value={form.method}
                        onChange={(e) => setForm((p) => ({ ...p, method: e.target.value }))}
                      >
                        <option value="on_demand">On demand</option>
                        <option value="annual">Annual</option>
                        <option value="separation">Separation / F&F</option>
                      </select>
                    </PolicyHintField>
                  </div>
                )}

                {step === 2 && (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <PolicyHintField
                      label="Minimum service (months)"
                      hint="From Date of Joining. Leave Group must also have Encashment Allowed."
                    >
                      <input
                        type="number"
                        min="0"
                        className={pwInput}
                        value={form.minServiceMonths}
                        onChange={(e) => setForm((p) => ({ ...p, minServiceMonths: e.target.value }))}
                      />
                    </PolicyHintField>
                    <PolicyHintField
                      label="Min balance to keep (days)"
                      hint="Employee must retain at least this many days after encashment."
                    >
                      <input
                        type="number"
                        min="0"
                        className={pwInput}
                        value={form.minBalanceDays}
                        onChange={(e) => setForm((p) => ({ ...p, minBalanceDays: e.target.value }))}
                      />
                    </PolicyHintField>
                    <PolicyHintField label="Min days this request" hint="Smallest number of days on one request.">
                      <input
                        type="number"
                        min="1"
                        className={pwInput}
                        value={form.minEncashDays}
                        onChange={(e) => setForm((p) => ({ ...p, minEncashDays: e.target.value }))}
                      />
                    </PolicyHintField>
                    <PolicyHintField label="Max days this request" hint="Leave blank for no per-request cap besides Leave Group.">
                      <input
                        type="number"
                        min="0"
                        className={pwInput}
                        value={form.maxEncashDays}
                        onChange={(e) => setForm((p) => ({ ...p, maxEncashDays: e.target.value }))}
                      />
                    </PolicyHintField>
                    <PolicyHintField label="Max days per year" hint="Calendar-year cap across all requests. Blank = none.">
                      <input
                        type="number"
                        min="0"
                        className={pwInput}
                        value={form.maxAnnualDays}
                        onChange={(e) => setForm((p) => ({ ...p, maxAnnualDays: e.target.value }))}
                      />
                    </PolicyHintField>
                    {form.method === "annual" && (
                      <PolicyHintField label="Annual pay month" hint="Month (01–12) when annual encashment is paid.">
                        <input
                          className={pwInput}
                          value={form.annualPayMonth}
                          onChange={(e) => setForm((p) => ({ ...p, annualPayMonth: e.target.value }))}
                          placeholder="03"
                        />
                      </PolicyHintField>
                    )}
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!form.allowDuringEmployment}
                        onChange={(e) => setForm((p) => ({ ...p, allowDuringEmployment: e.target.checked }))}
                      />
                      Allow during employment
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!form.allowOnSeparation}
                        onChange={(e) => setForm((p) => ({ ...p, allowOnSeparation: e.target.checked }))}
                      />
                      Allow on separation
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!form.carryForwardEligible}
                        onChange={(e) => setForm((p) => ({ ...p, carryForwardEligible: e.target.checked }))}
                      />
                      Encash carry-forward balance
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!form.includePendingLeave}
                        onChange={(e) => setForm((p) => ({ ...p, includePendingLeave: e.target.checked }))}
                      />
                      Include pending leave applications
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!form.employeeCanRequest}
                        onChange={(e) => setForm((p) => ({ ...p, employeeCanRequest: e.target.checked }))}
                      />
                      Employee can request
                    </label>
                  </div>
                )}

                {step === 3 && (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <PolicyHintField
                      label="Salary basis"
                      hint="Wage used to compute per-day rate from the assigned structure."
                    >
                      <select
                        className={pwInput}
                        value={form.salaryBasis}
                        onChange={(e) => setForm((p) => ({ ...p, salaryBasis: e.target.value }))}
                      >
                        <option value="basic">Basic</option>
                        <option value="basic_da">Basic + DA</option>
                        <option value="gross">Gross</option>
                        <option value="ctc">CTC</option>
                      </select>
                    </PolicyHintField>
                    <PolicyHintField
                      label="Per-day method"
                      hint="Monthly ÷ 30, or monthly ÷ calendar days of the pay month."
                    >
                      <select
                        className={pwInput}
                        value={form.perDayMethod}
                        onChange={(e) => setForm((p) => ({ ...p, perDayMethod: e.target.value }))}
                      >
                        <option value="monthly_div_30">Monthly ÷ 30</option>
                        <option value="monthly_div_calendar">Monthly ÷ calendar days</option>
                      </select>
                    </PolicyHintField>
                    <PolicyHintField label="Encashment earning head" hint="Payslip earning code for in-service encashment.">
                      <input
                        className={pwInput}
                        value={form.encashmentEarning}
                        onChange={(e) => setForm((p) => ({ ...p, encashmentEarning: e.target.value }))}
                      />
                    </PolicyHintField>
                    <PolicyHintField label="Reversal head" hint="Recovery code if an approved encashment is reversed.">
                      <input
                        className={pwInput}
                        value={form.reversalRecovery}
                        onChange={(e) => setForm((p) => ({ ...p, reversalRecovery: e.target.value }))}
                      />
                    </PolicyHintField>
                    <PolicyHintField label="F&F earning head" hint="Earning code when encashment is paid on Full & Final.">
                      <input
                        className={pwInput}
                        value={form.fnfEarning}
                        onChange={(e) => setForm((p) => ({ ...p, fnfEarning: e.target.value }))}
                      />
                    </PolicyHintField>
                    <label className="flex items-center gap-2 text-sm md:col-span-2">
                      <input
                        type="checkbox"
                        checked={!!form.inServiceFullyTaxable}
                        onChange={(e) => setForm((p) => ({ ...p, inServiceFullyTaxable: e.target.checked }))}
                      />
                      In-service amount fully taxable
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!form.separationApplyExemption}
                        onChange={(e) => setForm((p) => ({ ...p, separationApplyExemption: e.target.checked }))}
                      />
                      Apply §10(10AA) split on separation
                    </label>
                    <PolicyHintField
                      label="Exemption regime"
                      hint="Government vs non-government cap. Limits are settings, not hardcoded."
                    >
                      <select
                        className={pwInput}
                        value={form.exemptionRegime}
                        onChange={(e) => setForm((p) => ({ ...p, exemptionRegime: e.target.value }))}
                      >
                        <option value="non_government">Non-government</option>
                        <option value="government">Government</option>
                      </select>
                    </PolicyHintField>
                    <PolicyHintField label="Notified exemption cap (₹)" hint="Company-entered §10(10AA) notified limit. Blank = use engine default later.">
                      <input
                        type="number"
                        min="0"
                        className={pwInput}
                        value={form.notifiedExemptionCap}
                        onChange={(e) => setForm((p) => ({ ...p, notifiedExemptionCap: e.target.value }))}
                      />
                    </PolicyHintField>
                  </div>
                )}

                {step === 4 && (
                  <PolicyWorkflowStepSection
                    darkMode={darkMode}
                    policy={form}
                    setPolicy={setForm}
                    workflowStepsTab={workflowTab}
                    setWorkflowStepsTab={setWorkflowTab}
                    policyRoleOptions={policyRoleOptions}
                    addWorkflowStep={addWorkflowStep}
                    removeWorkflowStep={(key, index) =>
                      setForm((prev) => ({
                        ...prev,
                        [key]: (prev[key] || []).filter((_, i) => i !== index),
                      }))
                    }
                    updateWorkflowStep={(key, index, field, value) =>
                      setForm((prev) => ({
                        ...prev,
                        [key]: (prev[key] || []).map((s, i) =>
                          i === index ? { ...s, [field]: value } : s
                        ),
                      }))
                    }
                    tabs={ENCASH_WORKFLOW_TABS}
                    approvalMethodOptions={LOAN_APPROVAL_METHODS}
                  />
                )}

                {step === 5 && (
                  <PolicyApplicabilityStepSection
                    darkMode={darkMode}
                    policy={form}
                    setPolicy={setForm}
                    branchesList={branchesList}
                    departmentsList={departmentsList}
                    designationsList={designationsList}
                    gradesList={gradesList}
                    categoriesList={categoriesList}
                    subDepartmentsList={subDepartmentsList}
                    showLocation={false}
                    showEmploymentTypesHelp={false}
                    onToggleEmploymentType={(value) => {
                      setForm((p) => {
                        const cur = p.employmentTypes || [];
                        return {
                          ...p,
                          employmentTypes: cur.includes(value)
                            ? cur.filter((x) => x !== value)
                            : [...cur, value],
                        };
                      });
                    }}
                  />
                )}
              </PolicyFormWizardShell>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
